import { useEffect, useState, useRef } from "react";
import { Plus, Loader2, Trash2, Edit, Save, AlertCircle, Image as ImageIcon, Upload, X } from "lucide-react";
import API from "../../api/api";
import BatchActionBar from "../../components/BatchActionBar";

export default function Avatars() {
  const [avatars, setAvatars] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  
  // Multi-delete state
  const [selectedIds, setSelectedIds] = useState([]);
  const [isBatchDeleting, setIsBatchDeleting] = useState(false);
  
  // Form State
  const [name, setName] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  
  const fileInputRef = useRef(null);

  const fetchAvatars = async () => {
    try {
      setLoading(true);
      setError("");
      const { data } = await API.get("/admin/avatars");
      if (!data?.success) throw new Error(data?.message || "Failed to load avatars");
      setAvatars(data.avatars || []);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || "Failed to load avatars");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAvatars();
  }, []);

  const handleToggleSelectAll = () => {
    if (selectedIds.length === avatars.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(avatars.map((a) => a._id));
    }
  };

  const handleToggleSelectRow = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const handleBatchDelete = async () => {
    try {
      setIsBatchDeleting(true);
      const { data } = await API.post("/admin/batch-delete", {
        entity: "avatars",
        ids: selectedIds,
      });
      if (data?.success) {
        setSelectedIds([]);
        fetchAvatars();
      }
    } catch (err) {
      setError(err?.response?.data?.message || err.message || "Batch delete failed");
    } finally {
      setIsBatchDeleting(false);
    }
  };

  const openAddModal = () => {
    setName("");
    setSelectedFile(null);
    setPreviewUrl("");
    setEditingId(null);
    setShowModal(true);
  };

  const openEditModal = (avatar) => {
    setName(avatar.name || "");
    setSelectedFile(null);
    setPreviewUrl(avatar.imageUrl || "");
    setEditingId(avatar._id);
    setShowModal(true);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!editingId && !selectedFile) {
      setError("Please select an avatar image to upload");
      return;
    }

    try {
      setSaving(true);
      setError("");

      const formData = new FormData();
      formData.append("name", name);
      if (selectedFile) {
        formData.append("avatar", selectedFile);
      }

      if (editingId) {
        const { data } = await API.put(`/admin/avatars/${editingId}`, formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
        if (!data?.success) throw new Error(data?.message || "Failed to update avatar");
      } else {
        const { data } = await API.post("/admin/avatars", formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
        if (!data?.success) throw new Error(data?.message || "Failed to add avatar");
      }

      setShowModal(false);
      setName("");
      setSelectedFile(null);
      setPreviewUrl("");
      setEditingId(null);
      fetchAvatars();
    } catch (err) {
      setError(err?.response?.data?.message || err.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this avatar?")) return;
    try {
      setSaving(true);
      setError("");
      const { data } = await API.delete(`/admin/avatars/${id}`);
      if (!data?.success) throw new Error(data?.message || "Failed to delete avatar");
      setAvatars((prev) => prev.filter((a) => a._id !== id));
      setSelectedIds((prev) => prev.filter((i) => i !== id));
    } catch (err) {
      setError(err?.response?.data?.message || err.message || "Delete failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 lg:pl-5 xl:pl-7 2xl:pl-0">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white">Avatars</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">Manage preset user avatars</p>
        </div>
        <div className="flex items-center gap-3">
          {avatars.length > 0 && (
            <label className="flex items-center gap-2 px-3 py-2 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg text-xs font-semibold text-gray-700 dark:text-gray-300 cursor-pointer">
              <input
                type="checkbox"
                checked={avatars.length > 0 && selectedIds.length === avatars.length}
                onChange={handleToggleSelectAll}
                className="w-4 h-4 rounded border-gray-300 dark:border-slate-600 text-pink-600 focus:ring-pink-500 cursor-pointer"
              />
              Select All
            </label>
          )}
          <button
            onClick={openAddModal}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-pink-500 to-rose-500 text-white rounded-lg shadow-lg shadow-pink-500/25 hover:opacity-95 transition-opacity"
          >
            <Plus className="w-4 h-4" />
            Add Avatar
          </button>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 px-4 py-3 rounded-lg bg-red-50 text-red-700 dark:bg-red-500/10 dark:text-red-300">
          <AlertCircle className="w-4 h-4" />
          <span>{error}</span>
        </div>
      )}

      {/* Main Avatar List Card Grid */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-200 dark:border-slate-700 p-6 shadow-sm">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 text-gray-500">
            <Loader2 className="w-8 h-8 animate-spin text-pink-500 mb-2" />
            <span>Loading avatars...</span>
          </div>
        ) : avatars.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-gray-500">
            <ImageIcon className="w-12 h-12 text-gray-300 dark:text-slate-600 mb-2" />
            <span className="text-lg font-medium">No Avatars Found</span>
            <span className="text-sm text-gray-400 mt-1">Get started by adding a new avatar!</span>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-6">
            {avatars.map((avatar) => {
              const isSelected = selectedIds.includes(avatar._id);
              return (
              <div 
                key={avatar._id} 
                className={`group relative flex flex-col items-center p-4 rounded-2xl border transition-all duration-300 ${isSelected ? "border-pink-500 bg-pink-50/30 dark:bg-pink-500/10" : "bg-gray-50 dark:bg-slate-900/50 border-gray-100 dark:border-slate-800 hover:shadow-md"}`}
              >
                {/* Selection Checkbox */}
                <div className="absolute top-2 left-2 z-10">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => handleToggleSelectRow(avatar._id)}
                    className="w-4 h-4 rounded border-gray-300 dark:border-slate-600 text-pink-600 focus:ring-pink-500 cursor-pointer"
                  />
                </div>

                <div className="relative w-24 h-24 mb-3 rounded-full overflow-hidden border-2 border-pink-500/10 group-hover:border-pink-500 transition-colors">
                  <img
                    src={avatar.imageUrl}
                    alt={avatar.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                
                <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 text-center truncate w-full max-w-[120px]">
                  {avatar.name || "Avatar"}
                </h3>
                
                {/* Actions overlay */}
                <div className="flex items-center gap-2 mt-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <button
                    onClick={() => openEditModal(avatar)}
                    className="p-1.5 rounded-lg bg-blue-50 text-blue-600 dark:bg-blue-500/10 dark:text-blue-300 hover:scale-105 transition-transform animate-scaleUp"
                    title="Edit"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(avatar._id)}
                    className="p-1.5 rounded-lg bg-red-50 text-red-600 dark:bg-red-500/10 dark:text-red-300 hover:scale-105 transition-transform animate-scaleUp"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Floating Multi-Delete Bar */}
      <BatchActionBar
        selectedCount={selectedIds.length}
        totalCount={avatars.length}
        onClearSelection={() => setSelectedIds([])}
        onSelectAll={handleToggleSelectAll}
        onDeleteSelected={handleBatchDelete}
        isDeleting={isBatchDeleting}
        entityName="avatars"
      />

      {/* Add / Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fadeIn">
          <div className="w-full max-w-md bg-white dark:bg-slate-800 rounded-2xl border border-gray-200 dark:border-slate-700 shadow-2xl overflow-hidden transform scale-100 transition-transform">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-slate-700">
              <h2 className="text-lg font-bold text-gray-800 dark:text-white">
                {editingId ? "Edit Avatar" : "Add New Avatar"}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 text-gray-500 dark:text-gray-400"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="space-y-1.5">
                <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">
                  Avatar Name
                </label>
                <input
                  type="text"
                  placeholder="e.g., Cool Guy, Casual Girl"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-900 text-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-all"
                />
              </div>

              {/* Upload area */}
              <div className="space-y-1.5">
                <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">
                  Avatar Image
                </label>
                
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  accept="image/*"
                  className="hidden"
                />

                {previewUrl ? (
                  <div className="relative w-36 h-36 mx-auto rounded-full overflow-hidden border-4 border-pink-500/20 group">
                    <img
                      src={previewUrl}
                      alt="Preview"
                      className="w-full h-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedFile(null);
                        setPreviewUrl("");
                      }}
                      className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center text-white transition-opacity font-medium text-sm"
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    className="flex flex-col items-center justify-center border-2 border-dashed border-gray-200 dark:border-slate-700 rounded-xl p-8 cursor-pointer hover:border-pink-500 dark:hover:border-pink-500 transition-colors bg-gray-50 dark:bg-slate-900/30"
                  >
                    <Upload className="w-8 h-8 text-gray-400 dark:text-slate-600 mb-2" />
                    <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Click to upload avatar</span>
                    <span className="text-xs text-gray-400 mt-1">PNG, JPG or WEBP up to 5MB</span>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-100 dark:border-slate-700">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:underline"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-pink-500 to-rose-500 text-white font-medium text-sm rounded-xl hover:opacity-90 disabled:opacity-60 shadow-lg shadow-pink-500/20"
                >
                  {saving ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : editingId ? (
                    <Save className="w-4 h-4" />
                  ) : (
                    <Plus className="w-4 h-4" />
                  )}
                  {editingId ? "Save Changes" : "Add Avatar"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
